import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AvailableBranches from "../../components/availableBranches";
import { AvailableBrands } from "../../components/availableBrands";
import PastOrders from "../../components/pastOrders";
import { getRestaurantBranchesAsync, getPickupBranchDetails } from "../../redux/features/restaurants/restaurantThunks";
import { setRestaurantBranches } from "../../redux/features/restaurants/restaurantSlice";

export const PickupOrder = ({ pastOrdersList, branchesData, isCheckout }) => {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedBranchName, setSelectedBranchName] = useState(null);
  const [selectedBranchArea, setSelectedBranchArea] = useState(null);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const previousRestaurantIdRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const restaurantBranches = useSelector(
    (state) => state.restaurants.restaurantBranches
  );

  const selectedBranchDetails = useSelector(
    (state) => state.restaurants.selectedBranchDetails
  );

  // Initialize state from selectedBranchDetails on mount
  useEffect(() => {
    if (selectedBranchDetails) {
      setSelectedRestaurantId(selectedBranchDetails.restaurant_id);
      setSelectedBranchId(selectedBranchDetails.branch_id);
      setSelectedBranchArea(selectedBranchDetails.area);
      setSelectedBranchName(selectedBranchDetails.name_en);
    }
  }, []); // Empty dependency array to run only on mount

  // Handle restaurant selection from AvailableBrands
  const handleRestaurantSelect = (restaurantId) => {
    // Only process if the restaurant actually changed
    if (restaurantId !== selectedRestaurantId) {
      setSelectedRestaurantId(restaurantId);
      // Reset selected branch when changing restaurant
      setSelectedBranchId(null);
      setSelectedBranchName(null);
      setSelectedBranchArea(null);
      // Clear existing branches when restaurant changes to prevent showing incorrect data
      dispatch(setRestaurantBranches(null));
    }
  };

  // Handle branch selection from AvailableBranches
  const handleBranchSelect = (branchId, branchName, branchArea) => {
    setSelectedBranchId(branchId);
    setSelectedBranchName(branchName);
    setSelectedBranchArea(branchArea);
  };

  // Filter past orders to only show pickup orders for the selected branch and restaurant
  const filteredPastOrders =
    pastOrdersList && pastOrdersList.length > 0
      ? pastOrdersList.filter((order) => {
          // For branch selection, filter by branch_id and service_type
          // if (selectedBranchId) {
          //   return (
          //     order.branch_id === selectedBranchId &&
          //     order.service_type === "pickup"
          //   );
          // }
          // If only restaurant is selected (no branch yet), show all pickup orders for that restaurant
          // else
          if (selectedRestaurantId) {
            return (
              order.restaurant_id === selectedRestaurantId &&
              order.service_type === "pickup"
            );
          }
          // If nothing selected, don't show any orders
          return false;
        })
      : [];

  // Track changes to selectedRestaurantId
  useEffect(() => {
    // Store current restaurant ID for future reference
    previousRestaurantIdRef.current = selectedRestaurantId;
  }, [selectedRestaurantId]);

  // Fetch restaurant branches when a restaurant is selected
  useEffect(() => {
    // Only fetch if we have a valid restaurant ID
    if (selectedRestaurantId) {
      setIsLoadingBranches(true);
      dispatch(getRestaurantBranchesAsync(selectedRestaurantId))
        .then(() => {
          setIsLoadingBranches(false);
        })
        .catch((error) => {
          console.error('Error fetching branches:', error);
          setIsLoadingBranches(false);
        });
    }
  }, [selectedRestaurantId, dispatch]);
  
  // Auto-select if only one branch, or show error if no branches
  useEffect(() => {
    // If there's exactly one branch and we're not in checkout mode, auto-select it and navigate
    if (restaurantBranches && restaurantBranches.length === 1 && !isCheckout && !selectedBranchId) {
      const branch = restaurantBranches[0];
      handleBranchSelect(branch.id, branch.name_en || branch.name, branch.area || '');
      
      // Fetch branch details and navigate
      const navigateToMenu = async () => {
        await dispatch(getPickupBranchDetails(branch.id));
        navigate("/place-order");
      };
      navigateToMenu();
    }
  }, [restaurantBranches, isCheckout, selectedBranchId, selectedRestaurantId, dispatch, navigate]);

  // Get branch name when branch ID changes
  useEffect(() => {
    if (selectedBranchId && restaurantBranches) {
      const branch = restaurantBranches.find(
        (branch) => branch.id === selectedBranchId
      );
      if (branch) {
        setSelectedBranchName(branch.name_en || branch.name);
      } else {
        setSelectedBranchName(null);
        setSelectedBranchId(null);
      }
    }
  }, [selectedBranchId, restaurantBranches]);

  return (
    <div className="w-full grid grid-cols-12 gap-4 mt-4">
      {/* Available Brands Column - always present when not in checkout */}
      {!isCheckout && (
        <div
          className={`${
            restaurantBranches && selectedRestaurantId
              ? selectedBranchId && !isCheckout
                ? "col-span-4"
                : "col-span-6"
              : "col-span-6"
          }`}
        >
          <AvailableBrands
            branchesData={branchesData}
            onSelectRestaurant={handleRestaurantSelect}
            selectedRestaurantId={selectedRestaurantId}
          />
        </div>
      )}

      {/* Available Branches Column or Loading/Error State */}
      {selectedRestaurantId && (
        <div
          className={`${
            restaurantBranches && selectedRestaurantId
              ? selectedBranchId && !isCheckout
                ? "col-span-4"
                : "col-span-6"
              : "col-span-6"
          }`}
        >
          {isLoadingBranches ? (
            <div className="flex flex-col items-center justify-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              <p className="mt-4 text-gray-600">Loading branches...</p>
            </div>
          ) : restaurantBranches?.length > 0 ? (
            <AvailableBranches
              branches={restaurantBranches}
              onBranchSelect={handleBranchSelect}
              selectedBranchId={selectedBranchId}
              selectedBranchName={selectedBranchName}
              selectedBranchArea={selectedBranchArea}
              isCheckout={isCheckout}
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg">
              <svg className="w-16 h-16 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-semibold text-red-800 mb-2">No Branches Available</h3>
              <p className="text-sm text-red-600 text-center max-w-md">
                This restaurant currently has no branches available for pickup. Please select a different restaurant.
              </p>
            </div>
          )}
        </div>
      )}
      {/* Past Orders Column - conditionally rendered */}
      {selectedBranchId && !isCheckout && (
        <div className="col-span-4">
          <PastOrders
            pastOrdersList={filteredPastOrders}
            isPickup={true}
            selectedBranchId={selectedBranchId}
            selectedBranchName={selectedBranchName}
            selectedRestaurantId={selectedRestaurantId}
            selectedBranchArea={selectedBranchArea}
          />
        </div>
      )}
    </div>
  );
};
